<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { RouterLink } from 'vue-router'
import { Sparkles, TrendingUp, ShieldCheck, BadgeCheck, Radio, ArrowRight, Check, AlertTriangle, RefreshCw, Lock } from 'lucide-vue-next'
import LiveHero from '@/components/home/LiveHero.vue'
import CategoryTiles from '@/components/home/CategoryTiles.vue'
import AuctionRail from '@/components/home/AuctionRail.vue'
import QuickLinks from '@/components/home/QuickLinks.vue'
import DealOfDay from '@/components/home/DealOfDay.vue'
import BidBottomSheet from '@/components/auction/BidBottomSheet.vue'
import { useAuctionStore } from '@/stores/auction'
import { useActivityStore } from '@/stores/activity'
import { useFeatureStore } from '@/stores/feature'
import { useI18n } from '@/composables/useI18n'
import type { Auction } from '@/types'

const auctionStore = useAuctionStore()
const activityStore = useActivityStore()
const featureStore = useFeatureStore()
// "Every seller is KYC-checked" is only true while Admin requires KYC to sell.
const kycRequiredToSell = computed(() => !!featureStore.config.requirements?.kycRequiredToSell)
const { t } = useI18n()

const isBidModalOpen = ref(false)
const selectedBidAuction = ref<Auction | null>(null)

const active = computed(() => auctionStore.activeAuctions)

// Page state. `hasLoaded` keeps the very first paint on the skeleton instead of
// flashing an empty page before onMounted has started the request; a refetch
// that already has lots on screen keeps them rather than blanking the page.
const hasLoaded = ref(false)
const isInitialLoading = computed(() => !hasLoaded.value || (auctionStore.isLoading && active.value.length === 0))
const loadError = computed(() => (auctionStore.isLoading ? null : auctionStore.error))

// Hero: the lot with the most bids right now; the side list is whatever ends next.
const featured = computed<Auction | null>(() => auctionStore.mostActiveAuctions[0] || null)
const liveList = computed(() =>
  auctionStore.endingSoonAuctions
    .filter(a => a.id !== featured.value?.id)
    .slice(0, 4)
)

// Lots for the phone's first-screen rail: everything live, soonest first.
// (endingSoonAuctions only covers the next 24 h, so with a lot ending in two
// days the rail came out empty and the first screen had no lot on it at all.)
const liveRail = computed(() =>
  [...active.value].sort((a, b) => new Date(a.endsAt).getTime() - new Date(b.endsAt).getTime()).slice(0, 8)
)

const stats = computed(() => ({
  active: active.value.length,
  bids: active.value.reduce((sum, a) => sum + (a.bidCount || 0), 0),
  sellers: new Set(active.value.filter(a => a.seller?.kycStatus === 'verified').map(a => a.sellerId)).size,
}))

// Backend stores antiques/art under one id; count both toward the "art" tile.
const categoryAliases: Record<string, string[]> = { art: ['art', 'antiques'] }
const categoryCounts = computed(() => {
  const counts: Record<string, number> = {}
  for (const a of active.value) counts[a.category] = (counts[a.category] || 0) + 1
  for (const [slug, aliases] of Object.entries(categoryAliases)) {
    counts[slug] = aliases.reduce((sum, alias) => sum + (counts[alias] || 0), 0)
  }
  return counts
})

// Lot of the day: the highest-priced live lot that is not already the hero's
// featured lot; beside it, the next lots to end (neither of those two).
const lotOfDay = computed<Auction | null>(() => {
  const pool = active.value.filter(a => a.id !== featured.value?.id)
  if (!pool.length) return null
  return [...pool].sort((a, b) => (b.currentPrice?.minorUnits || 0) - (a.currentPrice?.minorUnits || 0))[0]
})
const endingNext = computed(() =>
  auctionStore.endingSoonAuctions
    .filter(a => a.id !== featured.value?.id && a.id !== lotOfDay.value?.id)
    .slice(0, 3)
)
const newest = computed(() => auctionStore.newestAuctions.slice(0, 8))
const mostActive = computed(() => auctionStore.mostActiveAuctions.filter(a => a.bidCount > 0).slice(0, 8))

function openBidSheet(lot: Auction) {
  selectedBidAuction.value = lot
  isBidModalOpen.value = true
}

async function loadAuctions() {
  await auctionStore.fetchAuctions({ perPage: 60, status: 'active' })
  hasLoaded.value = true
}

onMounted(async () => {
  await loadAuctions()
  activityStore.fetchInitial()
})
</script>

<template>
  <!-- The phone's top padding follows the real header height (--hdr-h) plus a
       small gap: a fixed 7.25rem left dead space above the categories. -->
  <div class="relative w-full max-w-full overflow-x-hidden bg-background px-4 pb-16 pt-[calc(var(--hdr-h,7.25rem)+0.75rem)] sm:px-5 lg:px-8 lg:pt-[7.5rem]">
    <!-- Sun wash: the palette's sun colour behind the hero, fading into the
         page ground (Admin > Design > Colours: sun colour). -->
    <div aria-hidden="true" class="pointer-events-none absolute inset-x-0 top-0 h-[46rem] bg-gradient-to-b from-sun/70 via-sun/35 to-transparent sm:h-[64rem]" />
    <div class="relative mx-auto w-full min-w-0 max-w-[1440px] space-y-8 sm:space-y-12 lg:space-y-16">
      <!-- LiveHero (with the page's h1) is desktop-only; phones still need
           one top-level heading for screen readers and search engines. -->
      <h1 class="sr-only lg:hidden">{{ t('landing.heroTitle') }}</h1>

      <!-- Loading: a hero-shaped skeleton instead of a hero reading 0 / 0 / 0.
           Desktop only, like LiveHero itself: on a phone it held 1,150px that
           vanished when the data came in and threw the whole page upwards. -->
      <div
        v-if="isInitialLoading"
        role="status"
        aria-live="polite"
        class="hidden gap-6 lg:grid rounded-[1.5rem] bg-ink p-5 sm:rounded-[2rem] sm:p-8 lg:grid-cols-12 lg:gap-8 lg:p-10"
      >
        <span class="sr-only">{{ t('common.loading') }}</span>

        <div class="space-y-4 lg:col-span-4">
          <div class="skeleton h-6 w-52 max-w-full rounded-full bg-white/[0.06]" />
          <div class="skeleton h-8 w-full rounded-lg bg-white/[0.06]" />
          <div class="skeleton h-8 w-4/5 rounded-lg bg-white/[0.06]" />
          <div class="skeleton h-14 w-full rounded-lg bg-white/[0.06]" />
          <div class="flex gap-2.5">
            <div class="skeleton h-12 w-40 rounded-xl bg-white/[0.06]" />
            <div class="skeleton h-12 w-32 rounded-xl bg-white/[0.06]" />
          </div>
          <div class="grid grid-cols-3 gap-3 border-t border-white/10 pt-5">
            <div v-for="stat in 3" :key="`hero-stat-${stat}`" class="space-y-2">
              <div class="skeleton h-3 w-full rounded bg-white/[0.06]" />
              <div class="skeleton h-6 w-12 max-w-full rounded bg-white/[0.06]" />
            </div>
          </div>
        </div>

        <div class="lg:col-span-5">
          <div class="skeleton h-[26rem] w-full rounded-card bg-white/[0.06] sm:h-[28rem] lg:h-full lg:min-h-[30rem]" />
        </div>

        <div class="space-y-2 lg:col-span-3">
          <div class="skeleton mb-3 h-4 w-32 rounded bg-white/[0.06]" />
          <div v-for="lot in 4" :key="`hero-lot-${lot}`" class="skeleton h-16 w-full rounded-xl bg-white/[0.06]" />
        </div>
      </div>

      <!-- The request failed: say so and offer a retry, in the hero's place -->
      <section
        v-else-if="loadError"
        role="alert"
        aria-labelledby="landing-error-title"
        class="rounded-[1.5rem] border border-border bg-surface px-6 py-12 text-center sm:rounded-[2rem] sm:px-10 sm:py-16"
      >
        <span class="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-xl bg-error/10 text-error">
          <AlertTriangle class="h-6 w-6" />
        </span>
        <h1 id="landing-error-title" class="mt-4 font-display text-2xl font-bold tracking-tight text-text-primary sm:text-3xl">
          {{ t('landing.loadError') || t('common.error') }}
        </h1>
        <p v-if="t('landing.loadErrorDesc')" class="mx-auto mt-2 max-w-md text-sm leading-relaxed text-text-secondary">
          {{ t('landing.loadErrorDesc') }}
        </p>
        <button
          type="button"
          class="mt-6 inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-bold text-onPrimary transition hover:bg-primary-hover active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
          @click="loadAuctions"
        >
          <RefreshCw class="h-4 w-4" />{{ t('common.retry') }}
        </button>
      </section>

      <!-- The hero is a desktop thing: on a phone its four-line headline and
           paragraph filled the whole first screen and pushed every lot below
           the fold, so there it gives way to the categories and the live
           lots (lalafo / house.kg / wildberries all open on content). -->
      <LiveHero v-else class="hidden lg:block" :featured="featured" :live-list="liveList" :stats="stats" @open-bid="openBidSheet" />

      <CategoryTiles :counts="categoryCounts" :class="loadError ? 'hidden' : ''" />

      <!-- One line of what makes this an auction and not a classifieds board -->
      <p v-if="!loadError" class="flex items-center gap-2.5 rounded-xl border border-success/25 bg-success/[0.07] px-4 py-3 text-[13px] font-medium leading-snug text-text-primary lg:hidden">
        <Lock class="h-4 w-4 shrink-0 text-success" />
        <span>{{ t('landing.escrowStrip') }}</span>
      </p>

      <!-- Live lots, right on the first screen -->
      <AuctionRail
        v-if="!loadError && liveRail.length"
        id="rail-live"
        class="lg:hidden"
        :title="t('landing.liveNow')"
        :subtitle="t('landing.liveNowSubtitle')"
        to="/auctions?status=live&sort=ending_soon"
        :items="liveRail"
        :icon="Radio"
        accent="live"
      />

      <!-- No active lots: the hero itself switches to its quiet pitch (LiveHero
           isQuiet), so there is no second "first lots coming soon" block here. -->

      <DealOfDay :lot="lotOfDay" :ending="endingNext" />

      <QuickLinks />

      <!-- Loading: rail-shaped skeletons where the lot rails will land, below
           the static blocks so nothing above them moves when the lots load -->
      <div v-if="isInitialLoading" aria-hidden="true" class="space-y-12 sm:space-y-16">
        <div v-for="row in 2" :key="`rail-skeleton-${row}`" class="space-y-4">
          <div class="flex items-center gap-3">
            <div class="skeleton h-9 w-9 shrink-0 rounded-xl" />
            <div class="skeleton h-6 w-44 max-w-[60%] rounded-lg" />
          </div>
          <div class="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
            <div v-for="card in 4" :key="`rail-skeleton-${row}-${card}`" class="skeleton h-72 rounded-card lg:h-80" />
          </div>
        </div>
      </div>

      <AuctionRail
        id="rail-newest"
        :title="t('landing.newest')"
        :subtitle="t('landing.newestSubtitle')"
        to="/auctions?sort=newest"
        :items="newest"
        :icon="Sparkles"
        accent="gold"
      />

      <!-- Sell band: a light brand tint with one solid call to action. It was
           a full-bleed block of the brand colour — the largest colour field on
           the page, and the one that read most like a betting promo. -->
      <section class="relative overflow-hidden rounded-[1.5rem] border border-primary/25 bg-primary/[0.07] sm:rounded-[2rem]">
        <div class="relative grid gap-6 p-6 sm:p-10 lg:grid-cols-2 lg:items-center">
          <div class="space-y-3">
            <h2 class="font-display text-2xl sm:text-3xl font-bold tracking-tight text-text-primary">{{ t('landing.sellTitle') }}</h2>
            <p class="max-w-lg text-sm sm:text-[15px] leading-relaxed text-text-secondary">{{ t('landing.sellSubtitle') }}</p>
            <RouterLink
              to="/sell"
              class="mt-2 inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-bold text-onPrimary transition hover:bg-primary-hover active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              {{ t('landing.sellCta') }}<ArrowRight class="h-4 w-4" />
            </RouterLink>
          </div>
          <ul class="grid gap-2.5">
            <li v-for="key in ['landing.sellPoint1', 'landing.sellPoint2', 'landing.sellPoint3']" :key="key" class="flex items-center gap-3 rounded-xl border border-border bg-surface px-4 py-3 text-sm font-medium text-text-primary">
              <span class="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-onPrimary"><Check class="h-3.5 w-3.5" /></span>
              {{ t(key) }}
            </li>
          </ul>
        </div>
      </section>

      <AuctionRail
        id="rail-active"
        :title="t('landing.mostActive')"
        :subtitle="t('landing.mostActiveSubtitle')"
        to="/auctions?sort=most_bids"
        :items="mostActive"
        :icon="TrendingUp"
        accent="ink"
      />

      <!-- Trust -->
      <section aria-labelledby="trust-title" class="rounded-[1.5rem] sm:rounded-[2rem] border border-border bg-surface p-6 sm:p-10">
        <div class="grid gap-8 lg:grid-cols-[1.1fr_2fr] lg:items-start">
          <div class="space-y-3">
            <span class="inline-flex items-center gap-1.5 rounded-full bg-primary/15 text-onPrimaryContainer px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em]">
              <ShieldCheck class="h-3.5 w-3.5" />Escrow
            </span>
            <h2 id="trust-title" class="font-display text-2xl sm:text-3xl font-bold tracking-tight text-text-primary">{{ t('landing.trustTitle') }}</h2>
            <p class="text-sm leading-relaxed text-text-secondary">{{ t('landing.trustSubtitle') }}</p>
          </div>
          <div class="grid gap-3 sm:grid-cols-3">
            <div class="rounded-card bg-background p-5">
              <span class="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/15 text-onPrimaryContainer"><ShieldCheck class="h-5 w-5" /></span>
              <h3 class="mt-4 font-display text-[15px] font-semibold text-text-primary">{{ t('landing.trustEscrowTitle') }}</h3>
              <p class="mt-1.5 text-[13px] leading-relaxed text-text-secondary">{{ t('landing.trustEscrowDesc') }}</p>
            </div>
            <div class="rounded-card bg-background p-5">
              <span class="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/15 text-onPrimaryContainer"><BadgeCheck class="h-5 w-5" /></span>
              <h3 class="mt-4 font-display text-[15px] font-semibold text-text-primary">{{ t('landing.trustKycTitle') }}</h3>
              <p class="mt-1.5 text-[13px] leading-relaxed text-text-secondary">{{ kycRequiredToSell ? t('landing.trustKycDesc') : t('landing.trustKycDescOptional') }}</p>
            </div>
            <div class="rounded-card bg-background p-5">
              <span class="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/15 text-onPrimaryContainer"><Radio class="h-5 w-5" /></span>
              <h3 class="mt-4 font-display text-[15px] font-semibold text-text-primary">{{ t('landing.trustLiveTitle') }}</h3>
              <p class="mt-1.5 text-[13px] leading-relaxed text-text-secondary">{{ t('landing.trustLiveDesc') }}</p>
            </div>
          </div>
        </div>
      </section>

    </div>

    <BidBottomSheet v-model="isBidModalOpen" :auction="selectedBidAuction" />
  </div>
</template>
